// Artist App — dedicated component for artist users (separate from TeamApp)
// Changes here won't affect TeamApp and vice versa
import { useState, useEffect, useRef, useMemo } from 'react';
import { Map, Clock, LogOut, AlertTriangle, Gift, Music, ArrowLeft, Users, Send, Image, Mic, Square, X, Camera } from 'lucide-react';
import { useStore } from '../store/useStore';
import { GanttTimeline } from './GanttTimeline';
import { MapViewMapbox } from './MapViewMapbox';
import { MapView } from './MapView';
import { EmailVerificationBanner } from './EmailVerificationBanner';
import { compressImage } from '../lib/imageCompression';
import { supabase } from '../lib/supabase';
import QRCode from 'react-qr-code';

export function ArtistApp() {
  const {
    currentUser, team, points, shifts, updateTeam, addIssue,
    messages, globalSettings, organizations,
    impersonatedTeamId, offerProducts, customQuotas, consumptions,
    addTeamDirectMessages, sharedTeamOrganizations,
  } = useStore();

  // ── Current Artist ──
  const effectiveTeamId = impersonatedTeamId || currentUser.teamId;
  const currentTeam = team.find(s => s.id === effectiveTeamId) || null;
  const parentArtist = currentTeam?.parentArtistId ? team.find(s => s.id === currentTeam.parentArtistId) : null;

  // ── Tabs ──
  const [activeTab, setActiveTab] = useState<'map' | 'timeline' | 'badge' | 'messages'>(() => {
    const tabParam = new URLSearchParams(window.location.search).get('tab');
    if (tabParam === 'timeline') return 'timeline';
    if (tabParam === 'messages') return 'messages';
    if (tabParam === 'badge') return 'badge';
    return 'map';
  });

  // ── Loading guard ──
  if (!currentTeam) return <div className="flex h-screen items-center justify-center bg-[#0A0B0E]"><p className="text-white">Artist not found</p></div>;

  // ── Orgs ──
  const currentOrg = organizations.find(o => o.id === currentTeam?.orgId);
  const teamOrgs = organizations.filter(o =>
    o.id === currentTeam?.orgId ||
    shifts.some(s => (s.teamId === currentTeam?.id || s.teamId === currentTeam?.parentArtistId) && s.orgId === o.id) ||
    (sharedTeamOrganizations || []).some(link => link.teamId === currentTeam?.id && link.orgId === o.id)
  );
  const myOrgIds = teamOrgs.map(o => o.id);
  // ── Logout ──
  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  // ── Consumption (same as TeamApp badge) ──
  const getEffectiveQuota = (teamId: string, prodName: string, product: typeof offerProducts[0]) => {
    const custom = customQuotas.find(q => q.teamId === teamId && q.productName === prodName);
    if (custom) return custom.customQuota;
    const catQuotas = product.categoryQuotas || [];
    if (catQuotas.length > 0) {
      const s = team.find(st => st.id === teamId);
      if (s?.categoryId) {
        const match = catQuotas.find(q => q.categoryId === s.categoryId);
        if (match) return match.quota;
      }
      return 0;
    }
    return product.defaultQuota;
  };

  // ── Issue reporting ──
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issueMessage, setIssueMessage] = useState('');
  const [issuePointId, setIssuePointId] = useState('');
  const handleReportIssue = () => {
    if (!issueMessage.trim()) return;
    addIssue({
      pointId: issuePointId || points[0]?.id || '',
      teamId: currentTeam.id,
      teamName: parentArtist?.name || currentTeam.name,
      message: issueMessage,
      type: 'alert',
      orgId: currentOrg?.id,
    });
    setIssueModalOpen(false);
    setIssueMessage('');
    setIssuePointId('');
  };

  // ── Messaging ──
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [convReply, setConvReply] = useState('');
  const [convSending, setConvSending] = useState(false);
  // Artist contacts: only assigned team member
  const staffContacts = useMemo(() => {
    if (!currentTeam.assignedTeamId) return [];
    const assigned = team.find(s => s.id === currentTeam.assignedTeamId);
    return assigned ? [{ id: assigned.id, name: assigned.name, role: 'staff' as const, photo: assigned.photo }] : [];
  }, [currentTeam.assignedTeamId, team]);

  const allContacts = [...staffContacts];
  const selectedConversation = selectedConversationId ? allContacts.find(c => c.id === selectedConversationId) : null;
  const conversationMessages = selectedConversation
    ? messages.filter(m => {
        if (m.teamId === currentTeam.id && m.senderName?.includes(selectedConversation.name)) return true;
        if (selectedConversation.role === 'staff' && m.teamId === currentTeam.id && m.fromTeam && m.senderName === currentTeam.name && m.recipientTeamId === selectedConversation.id) return true;
        return false;
      }).sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    : [];

  // Voice/Picture messaging
  const [messageImage, setMessageImage] = useState<string | null>(null);
  const [messageVoice, setMessageVoice] = useState<Blob | null>(null);
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  const handleImagePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file, 400, 400, 0.5);
    setMessageImage(compressed);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = recorder;
      const chunks: Blob[] = [];
      recorder.ondataavailable = e => chunks.push(e.data);
      recorder.onstop = () => { setMessageVoice(new Blob(chunks, { type: 'audio/webm' })); stream.getTracks().forEach(t => t.stop()); };
      recorder.start();
      setRecording(true);
    } catch (err) { alert('Microphone inaccessible'); }
  };
  const stopRecording = () => { mediaRecorderRef.current?.stop(); setRecording(false); };

  const handleSend = async () => {
    if ((!convReply.trim() && !messageImage && !messageVoice) || !selectedConversation) return;
    setConvSending(true);
    let targetTeamId = selectedConversation.id;
    let voiceDataUrl: string | null = null;
    if (messageVoice) {
      voiceDataUrl = await new Promise(resolve => { const r = new FileReader(); r.onloadend = () => resolve(r.result as string); r.readAsDataURL(messageVoice); });
    }
    const content = convReply.trim() || (messageVoice ? '🎤 Message vocal' : '📷 Image');
    addTeamDirectMessages([targetTeamId], content, currentTeam.name || 'Artist', currentTeam.id, messageImage, voiceDataUrl);
    setConvReply(''); setMessageImage(null); setMessageVoice(null); setConvSending(false);
  };

  // Auto-scroll chat
  useEffect(() => { if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight; }, [conversationMessages, selectedConversationId]);

  return (
    <div className={`flex flex-col ${impersonatedTeamId ? 'h-[calc(100dvh-2.5rem)]' : 'h-[100dvh]'} w-full bg-[#0A0B0E] text-white overflow-hidden`}>
      {currentTeam && !currentTeam.emailConfirmed && <EmailVerificationBanner currentTeam={currentTeam} updateTeam={updateTeam} />}

      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-[#14161B]/80 backdrop-blur-md border-b border-[#2D3139]/50 sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Music className="w-5 h-5 text-amber-400" />
            <span className="font-bold text-sm text-amber-400">Artiste</span>
          </div>
          <div className="min-w-0">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest block mb-0.5">Artiste 🎨</span>
            <span className="font-bold text-white text-sm block leading-tight truncate max-w-[150px]">{parentArtist?.name || currentTeam.name}</span>
          </div>
        </div>
        <button onClick={handleLogout} className="p-2 text-slate-400 hover:text-red-400"><LogOut className="w-4 h-4" /></button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'map' && (
          <div className="h-full">{globalSettings.mapProvider === 'google' ? <MapView isTeamView /> : <MapViewMapbox isTeamView />}</div>
        )}
        {activeTab === 'timeline' && <GanttTimeline />}
        {activeTab === 'badge' && (
          <div className="h-full overflow-y-auto p-4 space-y-4 flex flex-col items-center">
            {/* QR Code */}
            <div className="w-full max-w-sm relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-3xl blur opacity-25"></div>
              <div className="relative bg-[#14161B] border border-white/10 rounded-3xl p-6 shadow-2xl flex flex-col items-center">
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Votre QR Code Artiste</h3>
                <div className="bg-white p-3 rounded-2xl shadow-inner mb-4"><QRCode value={currentTeam.id} size={150} fgColor="#0A0B0E" bgColor="#FFFFFF" /></div>
                <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest text-center px-4">Présentez ce code aux points de ravitaillement</p>
              </div>
            </div>
            {/* Artist Identity Card */}
            <div className="w-full max-w-sm bg-gradient-to-br from-[#1E1B18] via-[#14161B] to-[#0A0B0E] border border-amber-500/20 rounded-3xl p-6 shadow-2xl">
              <div className="flex items-center gap-4 mb-4">
                {currentTeam.photo ? <img src={currentTeam.photo} className="w-16 h-16 rounded-full object-cover ring-2 ring-amber-500/50" /> :
                  <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center text-2xl font-bold text-amber-400 ring-2 ring-amber-500/50">{currentTeam.name[0]}</div>}
                <div>
                  <h3 className="text-xl font-bold text-white">{parentArtist?.name || currentTeam.name}</h3>
                  <p className="text-xs text-amber-400 font-bold uppercase tracking-wider mt-1">Artiste</p>
                  {currentTeam.roles && currentTeam.roles.length > 0 && <p className="text-[10px] text-slate-400 mt-1">{currentTeam.roles.join(', ')}</p>}
                </div>
              </div>
            </div>
            {/* Consumption */}
            {(() => {
              const orgProducts = offerProducts.filter(p => myOrgIds.includes(p.orgId || ''));
              if (orgProducts.length === 0) return null;
              return (
                <div className="w-full max-w-sm bg-[#14161B] border border-[#2D3139]/80 rounded-3xl p-5 shadow-2xl space-y-4">
                  <div className="flex items-center justify-between pb-2.5 border-b border-[#2D3139]/50">
                    <h4 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-1.5"><Gift className="w-4 h-4 text-amber-400" /> Consommations</h4>
                  </div>
                  {orgProducts.map(prod => {
                    const total = getEffectiveQuota(currentTeam.id, prod.name, prod);
                    const sum = consumptions.filter(c => c.teamId === currentTeam.id && c.productName === prod.name).reduce((s, c) => s + c.quantity, 0);
                    const remaining = Math.max(0, total - sum);
                    return (
                      <div key={prod.id} className="p-3 bg-[#1C1F26] rounded-2xl flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-200">{prod.name}</span>
                        <span className={`text-[10px] font-black font-mono ${remaining > 0 ? 'text-emerald-400' : 'text-rose-500'}`}>{remaining}/{total}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
        {activeTab === 'messages' && (
          <div className="h-full flex flex-col">
            {selectedConversation ? (
              <>
                {/* Chat header */}
                <div className="p-4 border-b border-[#2D3139] flex items-center gap-3">
                  <button onClick={() => setSelectedConversationId(null)} className="p-1"><ArrowLeft className="w-5 h-5 text-slate-400" /></button>
                  <span className="font-bold text-white text-sm">{selectedConversation.name}</span>
                </div>
                {/* Messages */}
                <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                  {conversationMessages.length === 0 && <p className="text-xs text-slate-500 text-center py-6">Aucun message</p>}
                  {conversationMessages.map(m => (
                    <div key={m.id} className={`flex flex-col ${m.senderName === currentTeam.name ? 'items-end' : 'items-start'}`}>
                      <div className={`rounded-2xl px-3.5 py-2 text-xs max-w-[80%] ${m.senderName === currentTeam.name ? 'bg-amber-600 text-white' : 'bg-[#1C1F26] border border-[#2D3139]/60 text-slate-200'}`}>
                        {m.content}
                        {m.imageUrl && <img src={m.imageUrl} className="mt-2 max-w-[160px] rounded-lg" />}
                        {m.voiceUrl && <audio src={m.voiceUrl} controls className="mt-2 h-7 max-w-[160px]" />}
                      </div>
                      <span className="text-[8px] text-slate-500 mt-1">{new Date(m.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  ))}
                </div>
                {/* Composer */}
                <div className="p-3 border-t border-[#2D3139]/30 space-y-2">
                  {(messageImage || messageVoice) && (
                    <div className="flex items-center gap-2 px-1">
                      {messageImage && <div className="relative"><img src={messageImage} className="h-12 rounded-lg" /><button onClick={() => setMessageImage(null)} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"><X className="w-2 h-2 text-white" /></button></div>}
                      {messageVoice && <div className="flex items-center gap-1 text-[10px] text-emerald-400"><span>🎤</span><audio src={URL.createObjectURL(messageVoice)} controls className="h-5 w-24" /><button onClick={() => setMessageVoice(null)}><X className="w-3 h-3 text-red-400" /></button></div>}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} />
                    <button onClick={() => fileInputRef.current?.click()} className="w-8 h-8 bg-[#1A1D23] border border-[#2D3139] text-slate-400 rounded-lg flex items-center justify-center"><Image className="w-3 h-3" /></button>
                    <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleImagePick} />
                    <button onClick={() => cameraInputRef.current?.click()} className="w-8 h-8 bg-[#1A1D23] border border-[#2D3139] text-slate-400 rounded-lg flex items-center justify-center"><Camera className="w-3 h-3" /></button>
                    {recording ? <button onClick={stopRecording} className="w-8 h-8 bg-red-500 text-white rounded-lg flex items-center justify-center animate-pulse"><Square className="w-3 h-3" /></button>
                      : <button onClick={startRecording} className="w-8 h-8 bg-[#1A1D23] border border-[#2D3139] text-slate-400 rounded-lg flex items-center justify-center"><Mic className="w-3 h-3" /></button>}
                    <textarea value={convReply} onChange={e => setConvReply(e.target.value)} placeholder="Message..." rows={1} className="flex-1 bg-[#1A1D23] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-xs resize-none"
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} />
                    <button onClick={handleSend} disabled={(!convReply.trim() && !messageImage && !messageVoice) || convSending}
                      className="w-10 h-10 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white rounded-xl flex items-center justify-center"><Send className="w-4 h-4" /></button>
                  </div>
                </div>
              </>
            ) : (
              <div className="h-full overflow-y-auto p-4 space-y-4">
                {/* Alert button */}
                <button onClick={() => setIssueModalOpen(true)} className="w-full flex items-center justify-center gap-2 py-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 font-bold text-xs rounded-xl">
                  <AlertTriangle className="w-4 h-4" /> Signaler une alerte
                </button>
                {/* Contact */}
                {staffContacts.length > 0 && (
                  <div>
                    <p className="text-[9px] font-black text-slate-500 uppercase tracking-wider mb-2">Contact</p>
                    {staffContacts.map(s => (
                      <button key={s.id} onClick={() => setSelectedConversationId(s.id)}
                        className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#2D3139]/50 hover:bg-[#1A1D23]">
                        {s.photo ? <img src={s.photo} className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-xs font-bold text-amber-400">{s.name[0]}</div>}
                        <span className="text-sm text-white">{s.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                {staffContacts.length === 0 && <p className="text-xs text-slate-500 text-center py-8">Aucun contact assigné. Votre membre team en charge sera désigné par l'administration.</p>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div className="flex border-t border-[#2D3139]/50 bg-[#14161B] shrink-0">
        {(['map', 'timeline', 'badge', 'messages'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 flex flex-col items-center gap-1 text-[10px] font-bold transition-colors ${activeTab === tab ? 'text-amber-400' : 'text-slate-500'}`}>
            {tab === 'map' && <Map className="w-4 h-4" />}
            {tab === 'timeline' && <Clock className="w-4 h-4" />}
            {tab === 'badge' && <Gift className="w-4 h-4" />}
            {tab === 'messages' && <Users className="w-4 h-4" />}
            {tab === 'map' ? 'Carte' : tab === 'timeline' ? 'Scènes' : tab === 'badge' ? 'Badge' : 'Messages'}
          </button>
        ))}
      </div>

      {/* Issue Modal */}
      {issueModalOpen && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-white font-bold mb-2">Signaler une alerte</h3>
            <p className="text-slate-400 text-xs mb-4">Cette alerte sera visible par le membre en charge, le manager et l'admin.</p>
            <select value={issuePointId} onChange={e => setIssuePointId(e.target.value)} className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-xs mb-3">
              <option value="">Sélectionner un point</option>
              {points.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <textarea value={issueMessage} onChange={e => setIssueMessage(e.target.value)} placeholder="Décrivez le problème..." rows={3}
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-xs mb-4 resize-none" />
            <div className="flex gap-2">
              <button onClick={() => setIssueModalOpen(false)} className="flex-1 py-2 bg-[#1A1D23] text-white rounded-xl text-xs">Annuler</button>
              <button onClick={handleReportIssue} disabled={!issueMessage.trim()}
                className="flex-1 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white rounded-xl text-xs font-bold">Envoyer l'alerte</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
